import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, LogOut, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Boton, TarjetaDeAjustes, CajaDeTexto, RecuadroDeEstado } from './alumno';
import {
  JOIN_CODE_LENGTH, copyDeFalloDeGrupo, conteoDeAlumnos, normalizeCodeInput,
  readPendingJoinCode, clearPendingJoinCode, olvidarCodigoUnido,
} from '../lib/groups';
import AvisoAlUnirse from './AvisoAlUnirse';
import { AVISO_PRIVACIDAD_VERSION } from '../lib/avisoPrivacidad';

/* PILOTO-1 (firma #79) — LA ENTRADA DISCRETA, y la ÚNICA superficie nueva que
 * ve el consumidor de hoy.
 *
 * DÓNDE VIVE: una tarjeta al final de `/settings` (SettingsPage), visible para
 * TODA cuenta registrada — con bandera institucional o sin ella. Es deliberado:
 * cualquiera puede ser alumno de alguien, así que esconderla detrás de un
 * permiso dejaría el círculo sin cerrar. Los guests no la ven porque no entran a
 * Ajustes (SettingsPage los rebota a /signup, línea 116).
 *
 * QUÉ PIDE: seis caracteres. NADA MÁS. El grupo no le pregunta al alumno ni un
 * dato nuevo — el nombre y el correo que el profesor verá son los que la cuenta
 * ya tenía desde que se registró.
 *
 * SIN GRUPOS Y SIN CÓDIGO PENDIENTE la tarjeta es una línea de texto y un campo:
 * discreta de verdad, no una sección que compita con Suscripción o Contraseña.
 */

export default function JoinGroupCard() {
  const { t } = useTranslation(['settings', 'dashboard', 'common']);
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [groups, setGroups] = useState([]);
  const [leaving, setLeaving] = useState('');
  // pantallas-alumno (G), P8 de la parte 2: la lectura que falla ya no se disfraza de "sin grupos".
  const [falloCarga, setFalloCarga] = useState(false);
  const [recargando, setRecargando] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/groups/memberships');
      setGroups(res.data.groups || []);
      setFalloCarga(false);
    } catch (err) {
      // Sin aviso flotante (esta tarjeta es un extra de Ajustes), pero tampoco una lista vacia que miente:
      // P8 pinta el recuadro I2 en el lugar de la lista, con su "Retry".
      setGroups([]);
      setFalloCarga(true);
    }
  }, []);

  const reintentar = async () => {
    setRecargando(true);
    try { await load(); } finally { setRecargando(false); }
  };

  useEffect(() => { load(); }, [load]);

  // El puente del deep link web: si alguien abrió /join/CODIGO sin sesión, el
  // código quedó guardado y aquí sale ya escrito para que solo tenga que
  // confirmar (ver lib/groups.js — es comodidad, no seguridad).
  useEffect(() => {
    const pendiente = readPendingJoinCode();
    // PILOTO-1C — el pre-llenado entra por la MISMA puerta que el tecleo. Lo que
    // se guardó fue el segmento crudo de la URL: si venía en minúsculas o con un
    // guión, el campo lo mostraba tal cual y el alumno leía algo distinto de lo
    // que su profesor le dictó.
    if (pendiente) setCode(normalizeCodeInput(pendiente));
  }, []);

  const join = async (e) => {
    e?.preventDefault();
    const entry = code.trim();
    if (!entry) return;
    setJoining(true);
    try {
      const res = await api.post('/groups/join', { code: entry, aviso_version: AVISO_PRIVACIDAD_VERSION });
      // post-t7-2c (D4): el Panel, si esta montado en esta pestana, vuelve a pedir el material de los grupos.
      window.dispatchEvent(new CustomEvent('studybuddy:grupos-cambiados'));
      setCode('');
      // PILOTO-1D — unirse suelta el pendiente Y el descarte DEL CODIGO AL QUE SE
      // ENTRO, que en esta tarjeta puede no ser el pendiente: aqui el alumno puede
      // teclear uno distinto del que quedo guardado.
      olvidarCodigoUnido(entry);
      toast.success(res.data.already_member
        ? t('dashboard:groups.toasts.already_member', { name: res.data.group?.name })
        : t('dashboard:groups.toasts.joined', { name: res.data.group?.name }));
      await load();
    } catch (err) {
      // PILOTO-1B — el codigo VENCIDO tiene su propio copy (el del backend viene
      // en ingles y sin traducir) y suelta el pendiente: un codigo muerto no
      // debe seguir saliendo pre-llenado en esta tarjeta manana.
      // PILOTO-1B — LOS DOS VEREDICTOS SE TRADUCEN, y el resto cae al `detail`.
      //
      // El 404 lo pintaban ya en español `/join/CODIGO` y el modal del empujón
      // (`groups.join_invalid`), y SOLO esta tarjeta lo dejaba pasar en el inglés
      // crudo del backend: la misma falla, tres superficies, dos idiomas. Es la
      // lección que la tanda L dejó firmada (Dashboard.js: «aquí se pintaba
      // inglés crudo del servidor»): traducir por código de estado y dejar el
      // `detail` como red de abajo, no como primera opción.
      //
      // Los otros `detail` en inglés del backend (grupo lleno, tope propio, 429)
      // siguen cayendo a la red de abajo: son de PILOTO-1 y traducirlos es una
      // tanda de copy aparte — queda declarado en reporte_piloto1b.md.
      //
      // PILOTO-1C — el mismo criterio, ahora en UNA función compartida por las
      // seis superficies (`copyDeFalloDeGrupo`), que además traduce el rechazo
      // del SELLO de sesión (403) en vez de pintar «Invalid CSRF token».
      const status = err.response?.status;
      if (status === 410 || status === 404) clearPendingJoinCode();
      toast.error(copyDeFalloDeGrupo(err, t, 'dashboard:groups.toasts.join_failed',
        { 410: 'join_expired', 404: 'join_invalid' }));
    } finally { setJoining(false); }
  };

  const leave = async (groupId) => {
    setLeaving(groupId);
    try {
      await api.post(`/groups/${groupId}/leave`);
      setGroups(prev => prev.filter(g => g.id !== groupId));
      toast.success(t('dashboard:groups.toasts.left'));
    } catch (err) {
      toast.error(copyDeFalloDeGrupo(err, t, 'dashboard:groups.toasts.leave_failed'));
    } finally { setLeaving(''); }
  };

  return (
    <TarjetaDeAjustes icono={Users} titulo={t('dashboard:groups.join_heading')} testid="join-group-section">
      {falloCarga ? (
        <RecuadroDeEstado tipo="no_cargo" testid="groups-load-failed" texto={t('settings:section_load_failed')}
          accion={{ texto: t('common:offline.no_connection.cta'), onClick: reintentar, icono: RotateCw, trabajando: recargando }} />
      ) : null}

      {groups.length > 0 && (
        <div className="flex flex-col -mt-1" data-testid="my-groups-list">
          {groups.map(g => {
            // pantallas-alumno (G), P14: "Teacher: {name}" cuando el nombre llega. El servidor lo manda siempre que
            // la cuenta del profe tiene nombre (`routes/groups.py`, `_owner_display_name`); sin nombre (cuenta sin
            // nombre o que ya no existe) llega vacio y se queda lo de hoy, el conteo.
            const maestro = typeof g.owner_name === 'string' ? g.owner_name.trim() : '';
            return (
              <div key={g.id} className="flex items-center justify-between gap-3 min-h-[56px] py-2 border-b border-[#1F1F1F] last:border-0"
                data-testid={`my-group-${g.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold text-[#F4F4F3] truncate" title={g.name}>{g.name}</p>
                  <p className="text-[13px] text-[#9A9A9A] truncate">
                    {maestro
                      ? t('dashboard:groups.taught_by', { name: maestro })
                      // post-t7-1 (A4): con cero, "Sin alumnos" (la regla del cero de las pantallas del profe)
                      : conteoDeAlumnos(t, g.member_count)}
                  </p>
                </div>
                <Boton variante="secundario" icono={LogOut} onClick={() => leave(g.id)} trabajando={leaving === g.id}
                  className="shrink-0" testid={`my-group-leave-${g.id}`}>
                  {t('dashboard:groups.leave_btn')}
                </Boton>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[13px] text-[#9A9A9A]">{t('dashboard:groups.join_hint')}</p>
      {/* post-t7-2b (A1, #613): el aviso va debajo del campo y antes del boton, asi que el campo y el boton
          quedan uno debajo del otro tambien en pantallas anchas (antes iban lado a lado). */}
      <form onSubmit={join} className="flex flex-col gap-3">
        {/* PILOTO-1C - EL CAMPO NORMALIZA MIENTRAS SE TECLEA: el valor y lo que se ve son lo mismo, y el largo
            real (6) es el tope. pantallas-alumno (G): la caja de la casa, con letra de ancho fijo y espaciada. */}
        <CajaDeTexto
          id="join-group-code"
          valor={code}
          onCambio={(v) => setCode(normalizeCodeInput(v))}
          maxLength={JOIN_CODE_LENGTH}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck="false"
          placeholder={t('dashboard:groups.code_placeholder')}
          aria-label={t('dashboard:groups.code_placeholder')}
          clase="font-mono tracking-[0.2em] uppercase"
          data-testid="join-group-input" />
        <AvisoAlUnirse />
        <Boton type="submit" aLoAncho disabled={!code.trim()} trabajando={joining} testid="join-group-submit">
          {t('dashboard:groups.join_btn')}
        </Boton>
      </form>
    </TarjetaDeAjustes>
  );
}
